import { MAZE_COLS, MAZE_ROWS } from '@/constants/maze';
import { useMazeCanvasTravel } from '@/hooks/use-maze-canvas-travel';
import { useMazeLiveMoveFlush } from '@/hooks/use-maze-live-move-flush';
import type { CometSample } from '@/lib/maze/maze-canvas-comet';
import { clearJunctionArrows, drawJunctionArrows } from '@/lib/maze/maze-canvas-junction-arrows';
import {
  applyDemoTutorialPixiStep,
  createEmptyDemoTutorialPixiHandles,
  disposeDemoTutorialPixiHandles,
  type DemoTutorialPixiHandles,
} from '@/lib/maze/maze-demo-tutorial-pixi';
import { getCurrentDemoLevel, useDemoStore } from '@/store/demo';
import { useLiveStore } from '@/store/live';
import { usePreviewStore } from '@/store/preview';
import { getMazePixiPalette, type MazeStagePixiPalette } from '@/theme/maze-stage-palette';
import { Direction, type MazeCell } from '@/types/maze';
import { MazeGamePhase } from '@/types/maze-phase';
import { isDemoTutorialActive } from '@/utils/maze/demo-tutorial';
import { findShortestPath } from '@/utils/maze/find-shortest-path';
import { MazePlayMode } from '@/utils/maze/maze-play-mode';
import * as PIXI from 'pixi.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { buildMazeCanvasScene } from './maze-canvas-build-scene';
import { createStartMovement } from './maze-canvas-start-movement';
import {
  PlayerState,
  type ActiveTravelSegment,
  type PendingLiveMove,
  type TravelArrivalOptions,
} from './maze-canvas-types';
import { useMazePixiApp } from './use-maze-pixi-app';

export function MazeCanvas({
  playMode,
  inputBlocked = false,
}: Readonly<{ playMode: MazePlayMode; inputBlocked?: boolean }>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const viewportRef = useRef<PIXI.Container | null>(null);
  const applyCanvasLayoutRef = useRef<(() => void) | null>(null);
  const resetViewportTransformRef = useRef<(() => void) | null>(null);
  const isDemo = playMode === MazePlayMode.Demo;
  const isPreview = playMode === MazePlayMode.Preview;

  const demoPhase = useDemoStore((s) => s.phase);
  const livePhase = useLiveStore((s) => s.phase);
  const previewPhase = usePreviewStore((s) => s.phase);
  const resolvePhase = (): typeof demoPhase => {
    if (isDemo) {
      return demoPhase;
    }
    if (isPreview) {
      return previewPhase;
    }
    return livePhase;
  };
  const phase = resolvePhase();

  const demoTimer = useDemoStore((s) => s.timer);
  const liveTimer = useLiveStore((s) => s.timer);
  const previewTimer = usePreviewStore((s) => s.timer);
  const resolveTimer = (): number => {
    if (isDemo) {
      return demoTimer;
    }
    if (isPreview) {
      return previewTimer;
    }
    return liveTimer;
  };
  const timer = resolveTimer();

  const liveMaze = useLiveStore((s) => (isDemo || isPreview ? null : (s.session?.maze ?? null)));
  const liveMazeId = liveMaze?.sessionMazeId ?? null;
  const demoLevel = useDemoStore((s) => (isDemo ? getCurrentDemoLevel(s) : null));
  const demoLevelId = demoLevel?.levelId ?? null;
  const previewLevel = usePreviewStore((s) => (isPreview ? s.previewLevel : null));
  const previewLevelId = previewLevel?.levelId ?? null;
  const hasActiveLiveSession = useLiveStore((s) => s.hasActiveLiveSession);
  const liveScore = useLiveStore((s) => s.score);
  const liveSession = useLiveStore((s) => s.session);
  const reachDemoGoal = useDemoStore((s) => s.reachDemoGoal);
  const completePreviewLevel = usePreviewStore((s) => s.completeLevel);
  const demoTutorialStep = useDemoStore((s) => s.demoTutorialStep);

  const {
    submitMoves,
    fetchStatus,
    advanceRoundAfterReachingEnd,
    setPhase,
    setScore,
    finishLiveSuccess,
    setTimer: setLiveTimer,
    skipRound,
    loseLife,
  } = useLiveStore(
    useShallow((s) => ({
      submitMoves: s.submitMoves,
      fetchStatus: s.fetchStatus,
      advanceRoundAfterReachingEnd: s.advanceRoundAfterReachingEnd,
      setPhase: s.setPhase,
      setScore: s.setScore,
      finishLiveSuccess: s.finishLiveSuccess,
      setTimer: s.setTimer,
      skipRound: s.skipRound,
      loseLife: s.loseLife,
    })),
  );
  const liveLives = useLiveStore((s) => s.lives);

  const onDemoGoal = useCallback(
    (timerValue: number) => {
      if (isPreview) {
        completePreviewLevel(timerValue);
        return;
      }
      reachDemoGoal(timerValue);
    },
    [completePreviewLevel, isPreview, reachDemoGoal],
  );

  const liveGoalActions = useMemo(() => {
    if (isDemo || isPreview) {
      return undefined;
    }
    return {
      setScore,
      finishLiveSuccess,
      setTimer: setLiveTimer,
      readSessionExpiryAt: () => liveSession?.expiryAt,
      readTotalScore: () => liveSession?.scoreboard.totalScore,
      readCurrentScore: () => liveScore,
    };
  }, [finishLiveSuccess, isDemo, isPreview, liveScore, liveSession, setLiveTimer, setScore]);

  const [, setMaze] = useState<MazeCell[][]>([]);
  const mazeRef = useRef<MazeCell[][]>([]);
  const playerStateRef = useRef<PlayerState>(PlayerState.IDLE);
  const positionRef = useRef({ x: 0, y: 0 });
  const goalCellRef = useRef({ x: MAZE_COLS - 1, y: MAZE_ROWS - 1 });
  const mazeColsRef = useRef(MAZE_COLS);
  const mazeRowsRef = useRef(MAZE_ROWS);
  const isLiveGameRef = useRef(false);
  const pendingLiveMovesRef = useRef<PendingLiveMove[]>([]);
  const trailRef = useRef<PIXI.Graphics | null>(null);
  const playerRef = useRef<PIXI.Container | null>(null);
  const mazeGridRef = useRef<number[][]>([]);
  const wallGlowRef = useRef<PIXI.Graphics | null>(null);
  const wallGlowInnerRef = useRef<PIXI.Graphics | null>(null);

  const trailPointsRef = useRef<Array<{ x: number; y: number }>>([]);
  const cometSamplesRef = useRef<CometSample[]>([]);
  const junctionLayerRef = useRef<PIXI.Container | null>(null);
  const beaconRef = useRef<PIXI.Container | null>(null);
  const tutorialLayerRef = useRef<PIXI.Container | null>(null);
  const tutorialPixiHandlesRef = useRef<DemoTutorialPixiHandles>(
    createEmptyDemoTutorialPixiHandles(),
  );
  const timerRef = useRef(timer);
  const phaseRef = useRef(phase);
  const moveToCellRef = useRef<(startX: number, startY: number, dir: Direction) => void>(() => {});
  const handleMoveRef = useRef<(direction: string | Direction) => void>(() => {});
  const activeTravelRef = useRef<ActiveTravelSegment | null>(null);
  const applyTravelArrivalRef = useRef<
    | ((
        nextX: number,
        nextY: number,
        direction: Direction,
        recordLiveMoveOnArrival: boolean,
        options: TravelArrivalOptions,
      ) => void)
    | null
  >(null);
  const junctionArrowRevealTokenRef = useRef(0);
  const demoGoalHandledRef = useRef(false);
  const inputBlockedRef = useRef(inputBlocked);
  const lastMoveInputAtRef = useRef(0);
  const mazePaletteRef = useRef<MazeStagePixiPalette>(getMazePixiPalette());

  useEffect(() => {
    inputBlockedRef.current = inputBlocked;
  }, [inputBlocked]);

  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const clearJunctionArrowsOnLayer = useCallback(() => {
    const layer = junctionLayerRef.current;
    if (layer) {
      clearJunctionArrows(layer);
    }
  }, []);

  const showJunctionArrowsAt = useCallback((x: number, y: number) => {
    const junctionLayer = junctionLayerRef.current;
    const row = mazeRef.current[y];
    if (!junctionLayer || !row) {
      return;
    }
    drawJunctionArrows(junctionLayer, row, x, y, mazePaletteRef.current);
  }, []);

  const {
    flushPendingLiveMoves,
    cancelIdleFlush,
    scheduleIdleFlush,
    flushPendingLiveMovesImmediate,
  } = useMazeLiveMoveFlush({
    isLiveGameRef,
    pendingLiveMovesRef,
    submitMoves,
    fetchStatus,
  });

  useMazeCanvasTravel({
    mazeRef,
    playerStateRef,
    positionRef,
    goalCellRef,
    isLiveGameRef,
    pendingLiveMovesRef,
    trailPointsRef,
    playerRef,
    activeTravelRef,
    applyTravelArrivalRef,
    moveToCellRef,
    junctionArrowRevealTokenRef,
    demoGoalHandledRef,
    timerRef,
    flushPendingLiveMoves,
    flushPendingLiveMovesImmediate,
    scheduleIdleFlush,
    cancelIdleFlush,
    showJunctionArrowsAt,
    advanceRoundAfterReachingEnd,
    skipRound,
    lives: liveLives,
    loseLife,
    setPhase,
    phaseRef,
    onDemoGoal,
    liveGoalActions,
  });

  const startMovement = useCallback(
    (direction: Direction) => {
      createStartMovement({
        lastMoveInputAtRef,
        playerStateRef,
        activeTravelRef,
        playerRef,
        applyTravelArrivalRef,
        goalCellRef,
        positionRef,
        mazeRef,
        junctionArrowRevealTokenRef,
        moveToCellRef,
        cancelIdleFlush,
        clearJunctionArrows: clearJunctionArrowsOnLayer,
      })(direction);
    },
    [cancelIdleFlush, clearJunctionArrowsOnLayer],
  );

  const hasPlayableMaze = useCallback((): boolean => {
    if (isDemo) {
      return demoLevel !== null;
    }
    if (isPreview) {
      return previewLevel !== null;
    }
    return hasActiveLiveSession && liveMaze !== null;
  }, [demoLevel, hasActiveLiveSession, isDemo, isPreview, liveMaze, previewLevel]);

  const initializeGame = useCallback(() => {
    const app = appRef.current;
    if (!app || !hasPlayableMaze()) {
      return;
    }
    lastMoveInputAtRef.current = 0;
    demoGoalHandledRef.current = false;
    buildMazeCanvasScene(
      app,
      playMode,
      {
        liveMaze: isDemo || isPreview ? null : liveMaze,
        demoLevel: isDemo ? demoLevel : null,
        previewLevel: isPreview ? previewLevel : null,
      },
      setMaze,
      {
        viewportRef,
        goalCellRef,
        mazeColsRef,
        mazeRowsRef,
        isLiveGameRef,
        pendingLiveMovesRef,
        mazeRef,
        positionRef,
        playerStateRef,
        trailPointsRef,
        cometSamplesRef,
        junctionLayerRef,
        trailRef,
        playerRef,
        beaconRef,
        tutorialLayerRef,
        mazeGridRef,
        wallGlowInnerRef,
        wallGlowRef,
      },
      mazePaletteRef,
      showJunctionArrowsAt,
      applyCanvasLayoutRef,
    );
    resetViewportTransformRef.current?.();
    disposeDemoTutorialPixiHandles(tutorialPixiHandlesRef.current);
    tutorialPixiHandlesRef.current = createEmptyDemoTutorialPixiHandles();

    if (isDemo && isDemoTutorialActive(useDemoStore.getState().demoTutorialStep)) {
      applyDemoTutorialPixiStep(useDemoStore.getState().demoTutorialStep, {
        player: playerRef.current,
        beacon: beaconRef.current,
        tutorialLayer: tutorialLayerRef.current,
        pathCells: findShortestPath(
          demoLevel!.mazeGrid,
          demoLevel!.startRow,
          demoLevel!.startCol,
          demoLevel!.endRow,
          demoLevel!.endCol,
        ),
        handles: tutorialPixiHandlesRef.current,
        gridRows: demoLevel!.rows,
        gridCols: demoLevel!.cols,
      });
    }
  }, [
    demoLevel,
    hasPlayableMaze,
    isDemo,
    isPreview,
    liveMaze,
    playMode,
    previewLevel,
    showJunctionArrowsAt,
  ]);

  const demoTutorialPath = useMemo(() => {
    if (!isDemo || !demoLevel) {
      return [];
    }
    return findShortestPath(
      demoLevel.mazeGrid,
      demoLevel.startRow,
      demoLevel.startCol,
      demoLevel.endRow,
      demoLevel.endCol,
    );
  }, [demoLevel, isDemo]);

  useEffect(() => {
    if (!isDemo || !isDemoTutorialActive(demoTutorialStep)) {
      disposeDemoTutorialPixiHandles(tutorialPixiHandlesRef.current);
      tutorialPixiHandlesRef.current = createEmptyDemoTutorialPixiHandles();
      return;
    }

    applyDemoTutorialPixiStep(demoTutorialStep, {
      player: playerRef.current,
      beacon: beaconRef.current,
      tutorialLayer: tutorialLayerRef.current,
      pathCells: demoTutorialPath,
      handles: tutorialPixiHandlesRef.current,
      gridRows: demoLevel?.rows ?? 0,
      gridCols: demoLevel?.cols ?? 0,
    });

    return () => {
      disposeDemoTutorialPixiHandles(tutorialPixiHandlesRef.current);
      tutorialPixiHandlesRef.current = createEmptyDemoTutorialPixiHandles();
    };
  }, [demoLevel?.cols, demoLevel?.rows, demoLevelId, demoTutorialPath, demoTutorialStep, isDemo]);

  useEffect(() => {
    handleMoveRef.current = (direction: string | Direction) => {
      if (inputBlockedRef.current || phaseRef.current !== MazeGamePhase.PLAYING) {
        return;
      }

      const parsedDirection = (() => {
        if (typeof direction !== 'string') {
          return direction;
        }
        const key = direction.toUpperCase() as keyof typeof Direction;
        const value = Direction[key];
        return typeof value === 'number' ? value : undefined;
      })();
      if (parsedDirection === undefined) {
        return;
      }

      startMovement(parsedDirection);
    };
  }, [startMovement]);

  useMazePixiApp(
    containerRef,
    appRef,
    viewportRef,
    applyCanvasLayoutRef,
    resetViewportTransformRef,
    handleMoveRef,
    trailRef,
    playerRef,
    cometSamplesRef,
    mazePaletteRef,
    wallGlowInnerRef,
    wallGlowRef,
    mazeGridRef,
    mazeColsRef,
    mazeRowsRef,
  );

  useEffect(() => {
    if (phase === MazeGamePhase.PLAYING && appRef.current && hasPlayableMaze()) {
      initializeGame();
    }
  }, [demoLevelId, hasPlayableMaze, initializeGame, liveMazeId, phase, playMode, previewLevelId]);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full touch-none select-none items-center justify-center overflow-hidden"
    />
  );
}
